import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, IconButton, TextField, Button, Stack, Alert, LinearProgress } from '@mui/material';
import { processTAIImage as processTAIImageApi } from '@/api/queries';
import { styled } from '@mui/material/styles';
import {
  X,
  ArrowRight,
  Undo2,
  Redo2,
  Download,
  MousePointer,
  Plus,
  FlipHorizontal,
  SquareStack,
  Minus,
  Brush,
  Eraser,
  RotateCcw,
  CircleDashed,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { useUIStore } from '../../store/useUIStore';
import { sendToGA } from '../../components/util/analytic';

const OverlayContainer = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  zIndex: 9999,
  backgroundColor: '#000',
  display: 'flex',
  flexDirection: 'column',
});

const CapturedImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
});

const CompareContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
});

const CompareImageContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
});

const CompareDivider = styled(Box)({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '4px',
  backgroundColor: '#ffffff',
  cursor: 'col-resize',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 0 10px rgba(0, 0, 0, 0.3)',
  '&::before': {
    content: '""',
    position: 'absolute',
    width: '20px',
    height: '40px',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  },
  '&::after': {
    content: '"⟷"',
    position: 'absolute',
    color: '#666',
    fontSize: '12px',
    fontWeight: 'bold',
  },
});

const CompareImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  userSelect: 'none',
  pointerEvents: 'none',
});

const CompareLabel = styled(Box)({
  position: 'absolute',
  top: '16px',
  padding: '4px 8px',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  fontSize: '12px',
  fontWeight: '600',
  borderRadius: '4px',
  zIndex: 999,
});

const SkeletonOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: `
    linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.5) 50%,
      transparent 100%
    )
  `,
  backgroundSize: '300% 100%',
  animation: 'shimmer 4s linear infinite',
  zIndex: 1001,
  '@keyframes shimmer': {
    '0%': {
      backgroundPosition: '-300% 0',
    },
    '100%': {
      backgroundPosition: '300% 0',
    },
  },
});

const ObjectThumbnail = styled('img')({
  position: 'fixed',
  width: '60px',
  height: '60px',
  objectFit: 'cover',
  borderRadius: '8px',
  border: '2px solid #2055FF',
  backgroundColor: 'white',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
  pointerEvents: 'none',
  zIndex: 10005,
  transform: 'translate(-50%, -50%)',
});

const CancelObjectButton = styled(Button)({
  position: 'fixed',
  top: '80px',
  right: '20px',
  backgroundColor: 'rgba(233, 49, 49, 0.9)',
  color: 'white',
  padding: '8px 16px',
  borderRadius: '20px',
  fontSize: '12px',
  fontWeight: '600',
  textTransform: 'none',
  zIndex: 10004,
  '&:hover': {
    backgroundColor: 'rgba(233, 49, 49, 1)',
  },
});

const PlacementOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  cursor: 'crosshair',
  zIndex: 200,
});

const DrawingCanvas = styled('canvas')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  cursor: 'crosshair',
  zIndex: 100,
  pointerEvents: 'auto',
});

const FloatingDrawingToolbar = styled(Box)({
  position: 'absolute',
  bottom: '240px', // Higher positioning to avoid main bottom bar
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'inline-flex',
  height: '36px',
  padding: '10px',
  alignItems: 'center',
  alignSelf: 'center',
  width: 'auto',
  gap: '10px',
  borderRadius: '8px',
  backgroundColor: '#F2F4F7',
  boxShadow: '0 4px 8px -2px rgba(16, 24, 40, 0.10), 0 2px 4px -2px rgba(16, 24, 40, 0.06)',
  zIndex: 10002,
  flexShrink: 0,
  // Mobile adjustments for proper spacing
  '@media (max-width: 600px)': {
    bottom: '280px', // Much higher on mobile to avoid bottom bar and controls
    left: '50%',
    transform: 'translateX(-50%)',
    maxWidth: 'calc(100vw - 40px)', // Ensure it fits on screen with padding
    overflowX: 'auto', // Allow horizontal scroll if needed
  },
});

const DrawingToolButton = styled(IconButton)(({ theme }) => ({
  width: '30px',
  height: '30px',
  padding: '0 8px',
  borderRadius: '8px',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: 'rgba(32, 85, 255, 0.1)',
  },
  '&.active': {
    backgroundColor: 'rgba(32, 85, 255, 0.1)',
  },
}));

const ToolSeparator = styled(Box)({
  width: '1px',
  height: '15.063px',
  backgroundColor: '#D0D5DD',
  position: 'relative',
});

const BrushSizeControl = styled(Box)({
  position: 'absolute',
  bottom: '180px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  padding: '12px',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderRadius: '12px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  zIndex: 10003,
  // Mobile adjustments
  '@media (max-width: 600px)': {
    bottom: '360px', // Much higher on mobile to avoid drawing toolbar
    left: '50%',
    transform: 'translateX(-50%)',
  },
});

const BrushSizeSlider = styled('input')({
  width: '100px',
  height: '4px',
  borderRadius: '2px',
  background: '#ddd',
  outline: 'none',
  opacity: 0.7,
  transition: 'opacity 0.2s',
  '&:hover': {
    opacity: 1,
  },
  '&::-webkit-slider-thumb': {
    appearance: 'none',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    background: '#2055FF',
    cursor: 'pointer',
  },
});

const BrushPreview = styled(Box)<{ size: number }>(({ size }) => ({
  width: `${Math.min(size, 40)}px`,
  height: `${Math.min(size, 40)}px`,
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 0, 0, 0.7)',
  border: '2px solid #ff0000',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 16,
  right: 16,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  color: '#000',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
  },
  zIndex: 10000,
}));

const MinimizeButton = styled(IconButton)<{ minimized?: boolean }>(({ theme, minimized }) => ({
  position: 'absolute',
  top: minimized ? -60 : -50, // Move higher when minimized for better visibility
  right: 20,
  backgroundColor: minimized ? 'rgba(32, 85, 255, 0.95)' : 'rgba(255, 255, 255, 0.95)', // Blue when minimized for better visibility
  color: minimized ? '#fff' : '#000',
  width: '44px', // Slightly larger when minimized
  height: '44px',
  borderRadius: '50%',
  boxShadow: minimized ? '0 4px 16px rgba(32, 85, 255, 0.4)' : '0 2px 8px rgba(0, 0, 0, 0.15)', // More prominent shadow when minimized
  border: minimized ? '2px solid rgba(255, 255, 255, 0.8)' : 'none', // White border when minimized
  '&:hover': {
    backgroundColor: minimized ? 'rgba(32, 85, 255, 1)' : 'rgba(255, 255, 255, 1)',
    transform: 'scale(1.05)', // Slight scale on hover
  },
  zIndex: 10002,
  transition: 'all 0.3s ease-in-out', // Smooth transitions
  // Only show on mobile
  display: 'none',
  '@media (max-width: 600px)': {
    display: 'flex',
  },
}));

const FloatingShowButton = styled(IconButton)(({ theme }) => ({
  position: 'fixed',
  bottom: 40,
  right: 20,
  backgroundColor: 'rgba(32, 85, 255, 0.95)',
  color: '#fff',
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  boxShadow: '0 6px 20px rgba(32, 85, 255, 0.4)',
  border: '2px solid rgba(255, 255, 255, 0.9)',
  '&:hover': {
    backgroundColor: 'rgba(32, 85, 255, 1)',
    transform: 'scale(1.1)',
  },
  zIndex: 10003,
  transition: 'all 0.3s ease-in-out',
  // Only show on mobile
  display: 'none',
  '@media (max-width: 600px)': {
    display: 'flex',
  },
}));

const FloatingBottomBar = styled(Stack)<{ minimized?: boolean }>(({ theme, minimized }) => ({
  position: 'absolute',
  bottom: 80, // Moved higher to prevent cutoff on mobile browsers
  left: '50%',
  transform: 'translateX(-50%)',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '10.544px', // Match Figma design
  padding: '10px',
  borderRadius: '15px',
  border: '0.753px solid #EAECF0',
  backgroundColor: '#FFF',
  boxShadow: '0 4px 8px -2px rgba(16, 24, 40, 0.10), 0 2px 4px -2px rgba(16, 24, 40, 0.06)',
  width: 'auto',
  maxWidth: '90vw',
  minWidth: '320px', // Ensure minimum width for proper layout
  zIndex: 10001,
  transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
  // Mobile adjustments - 20px padding and stretch across screen
  '@media (max-width: 600px)': {
    bottom: 60, // Even higher on mobile for better visibility
    left: 20, // Start from left edge with padding
    right: 20, // End at right edge with padding
    transform: minimized ? 'translateY(100%)' : 'none', // Slide down when minimized
    opacity: minimized ? 0 : 1,
    width: 'calc(100vw - 40px)', // Full width minus 20px padding on each side
    maxWidth: 'none',
    minWidth: 'none',
    padding: '20px', // Set 20px padding as requested
  },
}));

const PromptContainer = styled(Stack)(({ theme }) => ({
  height: '67px',
  padding: '10px',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  borderRadius: '8px',
  backgroundColor: '#F2F4F7',
  position: 'relative',
  flexDirection: 'row',
}));

const ControlsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  alignContent: 'center',
  gap: '10px',
  alignSelf: 'stretch',
  flexWrap: 'wrap',
  position: 'relative',
  width: 'auto',
  // Responsive behavior
  '@media (max-width: 600px)': {
    justifyContent: 'center',
  },
}));

const ControlGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  // Ensure groups don't break on mobile unless needed
  flexShrink: 0,
}));

const ControlButton = styled(Button)(({ theme }) => ({
  height: '34px',
  padding: '0 10px',
  alignItems: 'center',
  gap: '6px',
  borderRadius: '8px',
  backgroundColor: 'transparent',
  minWidth: 'auto',
  color: '#000',
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#F2F4F7',
    color: '#000', // Preserve text color on hover
  },
  '&.primary': {
    color: '#2055FF',
    '&:hover': {
      color: '#2055FF', // Preserve primary color on hover
      backgroundColor: '#F2F4F7',
    },
  },
  '&.danger': {
    color: '#E93131',
    '&:hover': {
      color: '#E93131', // Preserve danger color on hover
      backgroundColor: '#F2F4F7',
    },
  },
  '&.active': {
    backgroundColor: '#F2F4F7',
  },
}));

const Separator = styled(Box)({
  width: '1px',
  height: '15px',
  backgroundColor: '#D0D5DD',
});

const MobileConsoleContainer = styled(Box)({
  position: 'fixed',
  top: '60px',
  right: '10px',
  width: '350px',
  maxHeight: '300px',
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  color: '#00ff00',
  fontFamily: 'Monaco, monospace',
  fontSize: '10px',
  borderRadius: '8px',
  border: '1px solid #333',
  zIndex: 10010,
  overflow: 'hidden',
  '@media (max-width: 400px)': {
    width: '280px',
    right: '5px',
  },
});

const MobileConsoleHeader = styled(Box)({
  padding: '8px 12px',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  borderBottom: '1px solid #333',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: '11px',
  fontWeight: 'bold',
});

const MobileConsoleContent = styled(Box)({
  padding: '8px',
  maxHeight: '250px',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '2px',
  },
});

const MobileConsoleLog = styled(Box)<{ logType?: string }>(({ logType }) => ({
  marginBottom: '2px',
  fontSize: '9px',
  lineHeight: '1.2',
  wordBreak: 'break-word',
  color: logType === 'ERROR' ? '#ff6b6b' : logType === 'WARN' ? '#ffd93d' : '#00ff00',
}));

const MobileConsoleToggle = styled(Button)({
  position: 'fixed',
  top: '10px',
  right: '10px',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: '#00ff00',
  fontSize: '10px',
  padding: '4px 8px',
  minWidth: 'auto',
  borderRadius: '4px',
  zIndex: 10011,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
  },
});

export interface AIEditorProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AIEditor: React.FC<AIEditorProps> = ({ isOpen, onClose }) => {
  const [capturedImage, setCapturedImage] = useState<string>('');
  const [prompt, setPrompt] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [editedImage, setEditedImage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isCapturing, setIsCapturing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [cameraPose, setCameraPose] = useState<any>(null);
  const [compareMode, setCompareMode] = useState(false);
  const [dividerPosition, setDividerPosition] = useState(50); // Percentage from left
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedObject, setUploadedObject] = useState<string>(''); // Base64 image of uploaded object
  const [objectPlacementMode, setObjectPlacementMode] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [drawingMode, setDrawingMode] = useState(false);
  const [brushSize, setBrushSize] = useState(14);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingMask, setDrawingMask] = useState<string>(''); // Base64 image of drawing mask
  const [drawingTool, setDrawingTool] = useState<'brush' | 'eraser'>('brush');
  const [bottomBarMinimized, setBottomBarMinimized] = useState(false);

  // Mobile debugging console - auto-show on mobile devices for debugging
  const [mobileConsole, setMobileConsole] = useState<string[]>([]);
  const [showMobileConsole, setShowMobileConsole] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingCanvasRef = useRef<HTMLCanvasElement>(null);
  const compareContainerRef = useRef<HTMLDivElement>(null);
  const objectFileInputRef = useRef<HTMLInputElement>(null);

  // Enhanced logging for mobile debugging
  const mobileLog = useCallback((message: string, type: 'log' | 'error' | 'warn' = 'log') => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `[${timestamp}] ${type.toUpperCase()}: ${message}`;
    setMobileConsole((prev) => [...prev.slice(-30), logEntry]); // Keep last 30 logs

    // Also log to regular console
    console[type](message);
  }, []);

  // Initialize logging when component opens
  useEffect(() => {
    if (isOpen) {
      mobileLog('🔧 TAI Image Editor opened - Universal WebGL Capture');

      // Send Google Analytics event for AI editor opening
      sendToGA('event', {
        eventCategory: 'tai_design',
        eventAction: 'open',
        eventLabel: 'ai_editor_opened',
      });

      // Universal device detection logging
      const userAgent = navigator.userAgent;
      const isMobileDevice = /Mobile|Android|iPhone|iPad/.test(userAgent);
      const isChromeDevice = /Chrome/.test(userAgent);
      const screenInfo = `${window.screen.width}x${window.screen.height} @${window.devicePixelRatio}x`;

      mobileLog(`📱 Device Type: ${isMobileDevice ? 'Mobile' : 'Desktop'}`);
      mobileLog(`🌐 Browser: ${isChromeDevice ? 'Chrome' : 'Other'}`);
      mobileLog(`📺 Screen: ${screenInfo}`);
      mobileLog(`🎮 WebGL: ${!!window.WebGLRenderingContext ? 'Supported' : 'Not supported'}`);
      mobileLog(`💾 WebGL2: ${!!window.WebGL2RenderingContext ? 'Supported' : 'Not supported'}`);
    }
  }, [isOpen, mobileLog]);

  const captureScreenshot = useCallback(
    async (isRetry = false) => {
      if (isCapturing) return; // Prevent multiple simultaneous captures

      setIsCapturing(true);
      setError('');

      // Mobile debugging: Log capture start
      mobileLog(
        `🚀 Starting universal capture (attempt ${retryCount + 1}) - Device: ${
          /Mobile/.test(navigator.userAgent) ? 'Mobile' : 'Desktop'
        }`,
      );

      if (isRetry) {
        setRetryCount((prev) => prev + 1);
      }

      // Wait for the working showcase instance to be available
      const waitForShowcase = async (maxWait = 5000) => {
        const startTime = Date.now();
        while (Date.now() - startTime < maxWait) {
          const showcase = useUIStore.getState().showcase;
          if (
            showcase &&
            showcase.Renderer &&
            typeof showcase.Renderer.takeScreenShot === 'function'
          ) {
            console.log('🎯 Working showcase instance is ready');
            return showcase;
          }
          console.log('⏳ Waiting for working showcase instance...');
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
        console.log('⚠️ Timeout waiting for showcase instance');
        return null;
      };

      // Helper function to wait for rendering completion
      const waitForRender = (frames = 2) => {
        return new Promise<void>((resolve) => {
          let count = 0;
          const frame = () => {
            count++;
            if (count >= frames) resolve();
            else requestAnimationFrame(frame);
          };
          requestAnimationFrame(frame);
        });
      };

      // Universal WebGL capture solution - works for all devices
      const isMobile = /Mobile|Android|iPhone|iPad/.test(navigator.userAgent);
      const isChrome = /Chrome/.test(navigator.userAgent);

      // Universal timing based on device capabilities, not specific brands
      const getOptimalWaitFrames = () => {
        // More conservative timing for all devices to ensure WebGL completion
        if (isMobile) return 6; // Mobile devices need more time
        if (isChrome) return 4; // Chrome needs extra frames
        return 3; // Desktop default
      };

      // Universal WebGL context options optimized for all devices
      const getUniversalWebGLOptions = () => ({
        preserveDrawingBuffer: true, // Critical for capture
        antialias: false, // Reduces GPU load and improves compatibility
        alpha: false, // Prevents transparency issues
        premultipliedAlpha: false, // Prevents color corruption
        powerPreference: 'default', // Most compatible across devices
        failIfMajorPerformanceCaveat: false, // Don't fail on lower-end devices
        desynchronized: false, // Ensure synchronous rendering
        stencil: false, // Reduce GPU memory usage
        depth: true, // Maintain depth buffer for 3D accuracy
      });

      // Universal WebGL rendering completion - works for all devices
      const ensureWebGLRenderComplete = async (canvas: HTMLCanvasElement): Promise<boolean> => {
        try {
          const contextOptions = getUniversalWebGLOptions();
          const gl =
            canvas.getContext('webgl', contextOptions) ||
            canvas.getContext('webgl2', contextOptions) ||
            canvas.getContext('experimental-webgl', contextOptions);

          if (!gl) return false;

          // Universal GPU pipeline completion
          gl.finish(); // Wait for all operations to complete
          gl.flush(); // Force immediate execution

          // Wait for framebuffer to be ready
          await new Promise((resolve) => {
            const checkFramebuffer = () => {
              if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE) {
                resolve(true);
              } else {
                setTimeout(checkFramebuffer, 16); // Check every frame
              }
            };
            checkFramebuffer();
          });

          // Additional universal timing
          await new Promise((resolve) => setTimeout(resolve, 100));
          return true;
        } catch (err) {
          mobileLog(
            `WebGL completion check failed: ${err instanceof Error ? err.message : 'Unknown'}`,
            'warn',
          );
          return false;
        }
      };

      // Universal canvas capture with validation - works for all devices
      const captureCanvasUniversal = async (canvas: HTMLCanvasElement): Promise<string | null> => {
        try {
          // Ensure WebGL rendering is complete
          await ensureWebGLRenderComplete(canvas);

          // Multiple capture methods (ordered by reliability)
          const captureMethods = [
            // Method 1: Direct toDataURL (most reliable when buffer is preserved)
            () => canvas.toDataURL('image/png'),

            // Method 2: WebGL readPixels with manual canvas creation
            () => {
              const contextOptions = getUniversalWebGLOptions();
              const gl =
                canvas.getContext('webgl', contextOptions) ||
                canvas.getContext('webgl2', contextOptions);

              if (!gl) return null;

              const width = canvas.width;
              const height = canvas.height;
              const pixels = new Uint8Array(width * height * 4);

              gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

              // Create new canvas with captured pixels
              const captureCanvas = document.createElement('canvas');
              captureCanvas.width = width;
              captureCanvas.height = height;
              const ctx = captureCanvas.getContext('2d');

              if (ctx) {
                const imageData = ctx.createImageData(width, height);

                // Flip Y-axis (WebGL coordinates)
                for (let y = 0; y < height; y++) {
                  for (let x = 0; x < width; x++) {
                    const srcIndex = ((height - y - 1) * width + x) * 4;
                    const dstIndex = (y * width + x) * 4;

                    imageData.data[dstIndex] = pixels[srcIndex];
                    imageData.data[dstIndex + 1] = pixels[srcIndex + 1];
                    imageData.data[dstIndex + 2] = pixels[srcIndex + 2];
                    imageData.data[dstIndex + 3] = pixels[srcIndex + 3];
                  }
                }

                ctx.putImageData(imageData, 0, 0);
                return captureCanvas.toDataURL('image/png');
              }
              return null;
            },

            // Method 3: JPEG fallback
            () => canvas.toDataURL('image/jpeg', 0.95),
          ];

          for (let i = 0; i < captureMethods.length; i++) {
            try {
              const result = captureMethods[i]();
              if (result && result.length > 1000) {
                // Quick validation - create small test canvas
                const testImg = new Image();
                await new Promise<void>((resolve, reject) => {
                  testImg.onload = () => resolve();
                  testImg.onerror = reject;
                  testImg.src = result;
                });

                const testCanvas = document.createElement('canvas');
                testCanvas.width = 50;
                testCanvas.height = 50;
                const testCtx = testCanvas.getContext('2d');

                if (testCtx) {
                  testCtx.drawImage(testImg, 0, 0, 50, 50);

                  if (isValidCanvasData(testCanvas)) {
                    mobileLog(`✅ Universal canvas capture successful (method ${i + 1})`);
                    return result;
                  } else {
                    mobileLog(`⚠️ Method ${i + 1} produced invalid data, trying next...`, 'warn');
                  }
                }
              }
            } catch (err) {
              mobileLog(
                `Capture method ${i + 1} failed: ${err instanceof Error ? err.message : 'Unknown'}`,
                'warn',
              );
            }
          }

          return null;
        } catch (err) {
          mobileLog(
            `Universal canvas capture failed: ${err instanceof Error ? err.message : 'Unknown'}`,
            'error',
          );
          return null;
        }
      };

      // Helper function to validate canvas data (detect green screen)
      const isValidCanvasData = (canvas: HTMLCanvasElement): boolean => {
        try {
          const ctx = canvas.getContext('2d');
          if (!ctx) return false;

          const centerX = Math.floor(canvas.width / 2);
          const centerY = Math.floor(canvas.height / 2);

          const samples = [
            ctx.getImageData(centerX, centerY, 1, 1).data,
            ctx.getImageData(10, 10, 1, 1).data,
            ctx.getImageData(canvas.width - 10, 10, 1, 1).data,
            ctx.getImageData(10, canvas.height - 10, 1, 1).data,
            ctx.getImageData(canvas.width - 10, canvas.height - 10, 1, 1).data,
          ];

          const firstColor = `${samples[0][0]},${samples[0][1]},${samples[0][2]}`;
          const hasVariation = samples.some(
            (sample) => `${sample[0]},${sample[1]},${sample[2]}` !== firstColor,
          );

          const isGreenScreen = samples[0][0] === 0 && samples[0][1] > 200 && samples[0][2] === 0;
          const isBlackScreen = samples[0][0] < 10 && samples[0][1] < 10 && samples[0][2] < 10;

          return hasVariation && !isGreenScreen && !isBlackScreen;
        } catch (err) {
          return false;
        }
      };

      try {
        // Samsung debugging: Track which methods are attempted
        let methodsAttempted = [];

        const iframes = Array.from(document.getElementsByTagName('iframe'));
        const matterportIframe = iframes.find(
          (iframe) =>
            iframe.id.startsWith('iframe_') ||
            iframe.src?.includes('matterport') ||
            iframe.className?.includes('matterport') ||
            iframe.title?.includes('matterport'),
        ) as HTMLIFrameElement;

        mobileLog('🔍 Starting universal WebGL capture solution...');
        mobileLog(`📱 Device Info - Mobile: ${isMobile}, Chrome: ${isChrome}`);
        mobileLog(`🔧 Universal Settings - Wait frames: ${getOptimalWaitFrames()}, Max retries: 4`);
        mobileLog(
          `🎮 WebGL Support: ${!!window.WebGLRenderingContext}, WebGL2: ${!!window.WebGL2RenderingContext}`,
        );

        // Method 1: Matterport SDK (HIGHEST PRIORITY - most reliable)
        methodsAttempted.push('Method 1: Matterport SDK');
        const workingShowcase = await waitForShowcase();
        if (
          workingShowcase &&
          workingShowcase.Renderer &&
          workingShowcase.Renderer.takeScreenShot
        ) {
          console.log('✅ Found working showcase instance, taking screenshot...');

          try {
            // Universal timing optimization for all devices
            await waitForRender(getOptimalWaitFrames());

            // Universal delay based on device capabilities
            const universalDelay = isMobile ? 400 : 200; // Mobile devices need more time
            await new Promise((resolve) => setTimeout(resolve, universalDelay));

            mobileLog('📸 Attempting Matterport SDK capture...');

            const dataUrl = await workingShowcase.Renderer.takeScreenShot(undefined, {
              mattertags: false,
              sweeps: false,
            });

            if (dataUrl && typeof dataUrl === 'string' && dataUrl.length > 100) {
              mobileLog(
                `✅ SUCCESS! Matterport SDK capture worked ${
                  isSamsung ? '(Samsung optimized)' : '(Standard mode)'
                }`,
              );

              try {
                if (workingShowcase.Camera && workingShowcase.Camera.getPose) {
                  const pose = await workingShowcase.Camera.getPose();
                  setCameraPose(pose);
                  console.log('📍 Camera pose captured:', pose);
                }
              } catch (poseErr) {
                console.warn('Could not capture camera pose:', poseErr);
              }

              setCapturedImage(dataUrl);
              setHistory([dataUrl]);
              setHistoryIndex(0);
              return;
            }
          } catch (err) {
            mobileLog(
              `❌ Matterport SDK failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
              'error',
            );

            // Fallback with explicit resolution
            try {
              await waitForRender(2);
              const resolution = {
                width: matterportIframe?.offsetWidth || window.innerWidth,
                height: matterportIframe?.offsetHeight || window.innerHeight,
              };

              const dataUrl = await workingShowcase.Renderer.takeScreenShot(resolution, {
                mattertags: false,
                sweeps: false,
              });

              if (dataUrl && typeof dataUrl === 'string' && dataUrl.length > 100) {
                console.log('✅ Screenshot captured with explicit resolution!');
                setCapturedImage(dataUrl);
                setHistory([dataUrl]);
                setHistoryIndex(0);
                return;
              }
            } catch (err2) {
              console.error('❌ Fallback with resolution also failed:', err2);
            }
          }
        }

        // Method 1.5: Universal WebGL Canvas Capture (works for all devices)
        if (matterportIframe) {
          methodsAttempted.push('Method 1.5: Universal WebGL Canvas Capture');
          mobileLog('🎯 Method 1.5: Universal WebGL Canvas Capture...');
          try {
            // Find all WebGL canvases
            const allCanvases = [
              ...Array.from(document.querySelectorAll('canvas')),
              ...(matterportIframe.contentDocument
                ? Array.from(matterportIframe.contentDocument.querySelectorAll('canvas'))
                : []),
            ];

            // Filter for WebGL canvases with proper validation
            const webglCanvases = allCanvases
              .filter((canvas) => {
                const contextOptions = getUniversalWebGLOptions();
                const gl =
                  canvas.getContext('webgl', contextOptions) ||
                  canvas.getContext('webgl2', contextOptions) ||
                  canvas.getContext('experimental-webgl', contextOptions);
                return gl && canvas.width > 300 && canvas.height > 200;
              })
              .sort((a, b) => b.width * b.height - a.width * a.height);

            mobileLog(`🔧 Found ${webglCanvases.length} WebGL canvases for universal capture`);

            for (const canvas of webglCanvases) {
              mobileLog(`🎮 Testing canvas: ${canvas.width}x${canvas.height}`);

              try {
                // Wait for optimal frames
                await waitForRender(getOptimalWaitFrames());

                // Universal WebGL completion
                await ensureWebGLRenderComplete(canvas);

                // Try universal capture
                const dataUrl = await captureCanvasUniversal(canvas);
                if (dataUrl) {
                  mobileLog('✅ Universal WebGL canvas capture successful!');
                  setCapturedImage(dataUrl);
                  setHistory([dataUrl]);
                  setHistoryIndex(0);
                  return;
                }
              } catch (err) {
                mobileLog(
                  `Canvas ${canvas.width}x${canvas.height} failed: ${
                    err instanceof Error ? err.message : 'Unknown'
                  }`,
                  'warn',
                );
              }
            }

            // If direct canvas capture failed, try Matterport SDK with additional timing
            if (workingShowcase && workingShowcase.Renderer) {
              mobileLog('🔧 Trying Matterport SDK after WebGL optimization...');
              try {
                // Universal additional delay
                await new Promise((resolve) => setTimeout(resolve, 600));

                const dataUrl = await workingShowcase.Renderer.takeScreenShot(undefined, {
                  mattertags: false,
                  sweeps: false,
                });

                if (dataUrl && typeof dataUrl === 'string' && dataUrl.length > 100) {
                  mobileLog('✅ Matterport SDK successful after WebGL optimization!');
                  setCapturedImage(dataUrl);
                  setHistory([dataUrl]);
                  setHistoryIndex(0);
                  return;
                }
              } catch (err) {
                mobileLog(
                  `Post-WebGL Matterport capture failed: ${
                    err instanceof Error ? err.message : 'Unknown'
                  }`,
                  'error',
                );
              }
            }
          } catch (err) {
            mobileLog(
              `Universal WebGL capture failed: ${err instanceof Error ? err.message : 'Unknown'}`,
              'error',
            );
          }
        }

        // Method 2: Standard WebGL canvas capture with universal optimization
        if (matterportIframe) {
          methodsAttempted.push('Method 2: Standard WebGL Canvas Capture');
          mobileLog('🎯 Method 2: Standard WebGL Canvas Capture...');
          try {
            // Universal timing optimization
            await waitForRender(getOptimalWaitFrames());

            // Universal delay based on device type
            const standardDelay = isMobile ? 500 : 300;
            await new Promise((resolve) => setTimeout(resolve, standardDelay));

            const canvases = Array.from(document.querySelectorAll('canvas'));
            const iframeCanvases = matterportIframe.contentDocument
              ? Array.from(matterportIframe.contentDocument.querySelectorAll('canvas'))
              : [];

            const allCanvases = [...canvases, ...iframeCanvases];

            // Universal WebGL canvas filtering
            const webglCanvases = allCanvases
              .filter((canvas) => {
                const contextOptions = getUniversalWebGLOptions();
                const hasWebGL =
                  canvas.getContext('webgl', contextOptions) ||
                  canvas.getContext('webgl2', contextOptions) ||
                  canvas.getContext('experimental-webgl', contextOptions);
                const hasValidSize = canvas.width > 300 && canvas.height > 200;
                return hasWebGL && hasValidSize;
              })
              .sort((a, b) => b.width * b.height - a.width * a.height);

            for (const canvas of webglCanvases) {
              mobileLog(`Testing standard canvas: ${canvas.width} x ${canvas.height}`);

              try {
                // Use universal capture method
                const dataUrl = await captureCanvasUniversal(canvas);

                if (dataUrl) {
                  mobileLog('✅ Standard canvas capture successful');
                  setCapturedImage(dataUrl);
                  setHistory([dataUrl]);
                  setHistoryIndex(0);
                  return;
                } else {
                  mobileLog('⚠️ Canvas produced invalid data, trying next...', 'warn');
                }
              } catch (canvasErr) {
                mobileLog(
                  `Canvas capture failed: ${
                    canvasErr instanceof Error ? canvasErr.message : 'Unknown'
                  }`,
                  'warn',
                );

                // Advanced WebGL readPixels with Samsung-specific context handling
                const contextOptions = getWebGLContextOptions();
                const gl =
                  canvas.getContext('webgl', contextOptions) ||
                  canvas.getContext('webgl2', contextOptions) ||
                  canvas.getContext('experimental-webgl', contextOptions);

                if (gl) {
                  try {
                    // Enhanced completion waiting for Samsung devices
                    gl.finish();
                    gl.flush(); // Additional flush for Samsung GPU

                    const waitTime = isSamsung || isGalaxyS23 ? 500 : isMobileChrome ? 300 : 100;
                    await new Promise((resolve) => setTimeout(resolve, waitTime));

                    console.log(
                      `🔧 WebGL context type: ${gl.constructor.name}, Samsung: ${isSamsung}, Galaxy S23: ${isGalaxyS23}`,
                    );

                    const width = canvas.width;
                    const height = canvas.height;

                    // Samsung-specific framebuffer validation and pixel reading
                    const framebufferStatus = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
                    console.log(
                      `🔧 Framebuffer status: ${framebufferStatus}, Expected: ${gl.FRAMEBUFFER_COMPLETE}`,
                    );

                    if (framebufferStatus === gl.FRAMEBUFFER_COMPLETE) {
                      // Samsung-specific: Force render completion before reading
                      if (isSamsung || isGalaxyS23) {
                        console.log('🔧 Applying Samsung GPU pre-read operations...');
                        gl.finish();
                        gl.flush();
                        await new Promise((resolve) => setTimeout(resolve, 200));

                        // Force another render cycle for Samsung GPU
                        gl.clear(gl.COLOR_BUFFER_BIT);
                        gl.finish();
                        await new Promise((resolve) => setTimeout(resolve, 300));
                      }

                      const pixels = new Uint8Array(width * height * 4);

                      try {
                        gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

                        // Samsung-specific: Validate pixels aren't all the same color (green screen detection)
                        const firstPixel = [pixels[0], pixels[1], pixels[2], pixels[3]];
                        let hasVariation = false;
                        let greenPixels = 0;
                        const sampleSize = Math.min(1000, pixels.length / 4);

                        // Sample pixels to check for variation and green screen
                        for (let i = 0; i < sampleSize; i++) {
                          const pixelIndex = i * Math.floor(pixels.length / sampleSize / 4) * 4;
                          const r = pixels[pixelIndex];
                          const g = pixels[pixelIndex + 1];
                          const b = pixels[pixelIndex + 2];

                          // Check for variation
                          if (r !== firstPixel[0] || g !== firstPixel[1] || b !== firstPixel[2]) {
                            hasVariation = true;
                          }

                          // Check for green screen pattern
                          if (r === 0 && g > 200 && b === 0) {
                            greenPixels++;
                          }
                        }

                        const greenPercentage = (greenPixels / sampleSize) * 100;
                        console.log(
                          `🔧 Pixel analysis - First: [${firstPixel.join(
                            ',',
                          )}], Variation: ${hasVariation}, Green: ${greenPercentage.toFixed(1)}%`,
                        );

                        if (!hasVariation || greenPercentage > 50) {
                          console.log('⚠️ WebGL readPixels detected green screen or uniform color');

                          // Samsung-specific: Try reading from back buffer
                          if (isSamsung || isGalaxyS23) {
                            console.log('🔧 Attempting Samsung back buffer read...');
                            try {
                              gl.readBuffer && gl.readBuffer(gl.BACK);
                              await new Promise((resolve) => setTimeout(resolve, 100));
                              gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
                            } catch (backBufferErr) {
                              console.warn('Back buffer read failed:', backBufferErr);
                            }
                          }
                        }

                        const captureCanvas = document.createElement('canvas');
                        captureCanvas.width = width;
                        captureCanvas.height = height;
                        const ctx = captureCanvas.getContext('2d');

                        if (ctx) {
                          const imageData = ctx.createImageData(width, height);

                          // Flip image vertically (WebGL Y-axis is inverted)
                          for (let y = 0; y < height; y++) {
                            for (let x = 0; x < width; x++) {
                              const srcIndex = ((height - y - 1) * width + x) * 4;
                              const dstIndex = (y * width + x) * 4;

                              imageData.data[dstIndex] = pixels[srcIndex];
                              imageData.data[dstIndex + 1] = pixels[srcIndex + 1];
                              imageData.data[dstIndex + 2] = pixels[srcIndex + 2];
                              imageData.data[dstIndex + 3] = pixels[srcIndex + 3];
                            }
                          }

                          ctx.putImageData(imageData, 0, 0);

                          // Enhanced validation for Samsung devices
                          if (isValidCanvasData(captureCanvas)) {
                            const dataUrl = captureCanvas.toDataURL('image/png');
                            console.log(
                              '✅ Valid screenshot captured via enhanced WebGL readPixels',
                            );
                            setCapturedImage(dataUrl);
                            setHistory([dataUrl]);
                            setHistoryIndex(0);
                            return;
                          } else {
                            console.log('⚠️ Enhanced WebGL readPixels still produced invalid data');
                          }
                        }
                      } catch (readErr) {
                        console.warn('WebGL readPixels failed:', readErr);
                      }
                    }
                  } catch (glErr) {
                    console.log('WebGL readPixels failed:', glErr.message);
                  }
                }
              }
            }
          } catch (err) {
            console.warn('Canvas capture failed:', err);
          }
        }

        // Method 3: html2canvas fallback
        if ((window as any).html2canvas && matterportIframe) {
          console.log('🎯 Method 3: Trying html2canvas...');
          try {
            await waitForRender(2);
            const canvas = await (window as any).html2canvas(
              matterportIframe.parentElement || matterportIframe,
              {
                allowTaint: true,
                useCORS: true,
                scale: 1,
                logging: false,
              },
            );
            const dataUrl = canvas.toDataURL('image/png');
            if (dataUrl && dataUrl.length > 1000) {
              console.log('✅ Screenshot captured via html2canvas');
              setCapturedImage(dataUrl);
              setHistory([dataUrl]);
              setHistoryIndex(0);
              return;
            }
          } catch (err) {
            console.warn('html2canvas failed:', err);
          }
        }

        // Method 4: Canvas drawImage (same-origin only)
        if (matterportIframe) {
          console.log('��� Method 4: Trying canvas drawImage...');
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (ctx) {
              canvas.width = matterportIframe.offsetWidth || window.innerWidth;
              canvas.height = matterportIframe.offsetHeight || window.innerHeight;
              ctx.drawImage(matterportIframe as any, 0, 0);
              const dataUrl = canvas.toDataURL('image/png');
              console.log('✅ Screenshot captured via canvas drawImage');
              setCapturedImage(dataUrl);
              setHistory([dataUrl]);
              setHistoryIndex(0);
              return;
            }
          } catch (err) {
            console.warn('Canvas drawImage failed (cross-origin):', err);
          }
        }

        // Method 4.5: Force Matterport Scene Re-render (Samsung Galaxy S23 Last Resort)
        if ((isSamsung || isGalaxyS23) && workingShowcase) {
          console.log('🎯 Method 4.5: Force Matterport Scene Re-render for Samsung Galaxy S23...');
          try {
            // Force scene re-render by manipulating camera slightly
            if (
              workingShowcase.Camera &&
              workingShowcase.Camera.getPose &&
              workingShowcase.Camera.setPose
            ) {
              const currentPose = await workingShowcase.Camera.getPose();
              console.log('🔧 Got current camera pose, forcing re-render...');

              // Slightly modify position to force re-render
              const modifiedPose = {
                ...currentPose,
                position: {
                  x: currentPose.position.x + 0.001,
                  y: currentPose.position.y,
                  z: currentPose.position.z,
                },
              };

              // Set modified pose
              await workingShowcase.Camera.setPose(modifiedPose);

              // Wait for re-render
              await new Promise((resolve) => setTimeout(resolve, 1000));

              // Reset to original pose
              await workingShowcase.Camera.setPose(currentPose);

              // Wait for second re-render
              await new Promise((resolve) => setTimeout(resolve, 1500));

              // Force additional render cycles
              await waitForRender(getWaitFrames());

              // Now try capture
              const dataUrl = await workingShowcase.Renderer.takeScreenShot(undefined, {
                mattertags: false,
                sweeps: false,
              });

              if (dataUrl && typeof dataUrl === 'string' && dataUrl.length > 100) {
                console.log('✅ Samsung scene re-render capture successful!');
                setCapturedImage(dataUrl);
                setHistory([dataUrl]);
                setHistoryIndex(0);
                return;
              }
            }
          } catch (err) {
            console.warn('Samsung scene re-render method failed:', err);
          }
        }

        // Method 5: Generate placeholder
        console.log('🎯 Method 5: Generating placeholder...');
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas context not available');

        const width = matterportIframe?.offsetWidth || window.innerWidth;
        const height = matterportIframe?.offsetHeight || window.innerHeight;
        canvas.width = width;
        canvas.height = height;

        const gradient = ctx.createLinearGradient(0, 0, width, height);
        gradient.addColorStop(0, '#2c3e50');
        gradient.addColorStop(0.5, '#34495e');
        gradient.addColorStop(1, '#2c3e50');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);

        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.fillRect(width * 0.1, height * 0.3, width * 0.8, height * 0.4);

        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.font = `${Math.min(width, height) * 0.03}px Arial`;
        ctx.textAlign = 'center';
        ctx.fillText('Matterport View Captured', width / 2, height / 2);

        const dataUrl = canvas.toDataURL('image/png');
        console.log('✅ Placeholder screenshot generated');
        setCapturedImage(dataUrl);
        setHistory([dataUrl]);
        setHistoryIndex(0);
      } catch (error) {
        console.error('Screenshot capture failed:', error);

        // Universal retry logic for all devices
        const maxRetries = 4; // Generous retry count for all devices
        const retryDelay = isMobile ? 2000 : 1500; // Longer delay for mobile devices

        if (retryCount < maxRetries && !isRetry) {
          mobileLog(`🔄 Auto-retrying capture (attempt ${retryCount + 1}/${maxRetries + 1})...`);
          setTimeout(() => captureScreenshot(true), retryDelay);
        } else {
          const methodsInfo =
            methodsAttempted.length > 0
              ? ` Methods attempted: ${methodsAttempted.join(', ')}.`
              : '';
          const deviceInfo = isMobile
            ? ' Mobile devices may require multiple attempts due to WebGL optimization.'
            : '';

          const errorMessage =
            retryCount > 0
              ? `Failed to capture Matterport view after ${
                  retryCount + 1
                } attempts.${methodsInfo}${deviceInfo} You can proceed with the placeholder or retry.`
              : 'Failed to capture Matterport view. Trying to access the 3D renderer. You can retry or proceed with the placeholder.';
          setError(errorMessage);

          // Universal debugging info
          mobileLog('🔧 Universal Debug Report:', 'error');
          mobileLog(`Device: Mobile=${isMobile}, Chrome=${isChrome}`, 'error');
          mobileLog(
            `WebGL: ${!!window.WebGLRenderingContext}, WebGL2: ${!!window.WebGL2RenderingContext}`,
            'error',
          );
          mobileLog(`Methods: ${methodsAttempted.join(', ')}`, 'error');
          mobileLog(
            `Retries: ${retryCount}, Iframe: ${!!matterportIframe}, Showcase: ${!!workingShowcase}`,
            'error',
          );
        }
      } finally {
        setIsCapturing(false);
      }
    },
    [retryCount, isCapturing],
  ); // Improved version that fixes green screen issues

  const _originalCaptureScreenshotLogic = useCallback(
    async (isRetry = false) => {
      if (isCapturing) return; // Prevent multiple simultaneous captures

      setIsCapturing(true);
      setError('');

      if (isRetry) {
        setRetryCount((prev) => prev + 1);
      }

      // Wait for the working showcase instance to be available
      const waitForShowcase = async (maxWait = 3000) => {
        const startTime = Date.now();
        while (Date.now() - startTime < maxWait) {
          // Check for working showcase instance from store
          const showcase = useUIStore.getState().showcase;

          if (
            showcase &&
            showcase.Renderer &&
            typeof showcase.Renderer.takeScreenShot === 'function'
          ) {
            console.log('🎯 Working showcase instance is ready');
            return showcase;
          }

          console.log('⏳ Waiting for working showcase instance...');
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
        console.log('⚠️ Timeout waiting for showcase instance');
        return null;
      };

      try {
        // Find the Matterport iframe dynamically
        const iframes = Array.from(document.getElementsByTagName('iframe'));
        const matterportIframe = iframes.find(
          (iframe) =>
            iframe.id.startsWith('iframe_') ||
            iframe.src?.includes('matterport') ||
            iframe.className?.includes('matterport') ||
            iframe.title?.includes('matterport'),
        ) as HTMLIFrameElement;

        console.log('🔍 Looking for Matterport iframe and showcase...');
        console.log(
          'Found iframes:',
          iframes.map((i) => ({ id: i.id, src: i.src, className: i.className })),
        );

        // Debug: Check what's available in store
        console.log('Showcase availability check:');
        const showcase = useUIStore.getState().showcase;
        console.log('- store.showcase:', !!showcase);
        if (showcase) {
          console.log('Showcase object found with methods:');
          console.log('- Renderer:', !!showcase.Renderer);
          console.log('- Renderer.takeScreenShot:', typeof showcase.Renderer?.takeScreenShot);
          console.log('- Camera:', !!showcase.Camera);
          console.log('- Camera.getPose:', typeof showcase.Camera?.getPose);
        } else {
          console.log('❌ No showcase object found in any location');
        }

        if (!matterportIframe) {
          console.warn('⚠️ Matterport iframe not found, trying global showcase API');
        } else {
          console.log('✅ Found Matterport iframe:', matterportIframe.id);
          console.log('Iframe details:', {
            src: matterportIframe.src,
            width: matterportIframe.offsetWidth,
            height: matterportIframe.offsetHeight,
            style: matterportIframe.style.cssText,
          });
        }

        // Method 1: Use the same working pattern as the existing codebase
        console.log('🎯 Using working showcase pattern from existing codebase...');

        // Wait for and get the working showcase instance
        const workingShowcase = await waitForShowcase();

        if (
          workingShowcase &&
          workingShowcase.Renderer &&
          workingShowcase.Renderer.takeScreenShot
        ) {
          console.log('��� Found working showcase instance, taking screenshot...');

          try {
            // Use the exact same method as the working sdk.ts takeScreenshot function
            const dataUrl = await workingShowcase.Renderer.takeScreenShot(undefined, {
              mattertags: false,
              sweeps: false,
            });

            if (dataUrl && typeof dataUrl === 'string' && dataUrl.length > 100) {
              console.log('✅ Screenshot captured successfully!');
              console.log('Screenshot size:', dataUrl.length, 'characters');
              console.log('Data URL starts with:', dataUrl.substring(0, 50) + '...');

              // Also capture the current camera pose for accurate 3D tagging
              try {
                if (workingShowcase.Camera && workingShowcase.Camera.getPose) {
                  const pose = await workingShowcase.Camera.getPose();
                  setCameraPose(pose);
                  console.log('📍 Camera pose captured:', pose);
                }
              } catch (poseErr) {
                console.warn('Could not capture camera pose:', poseErr);
              }

              setCapturedImage(dataUrl);
              setHistory([dataUrl]);
              setHistoryIndex(0);
              return;
            } else {
              console.warn('⚠��� Showcase returned empty or invalid screenshot');
            }
          } catch (err) {
            console.error('❌ Showcase takeScreenShot failed:', err);

            // Try with explicit resolution as fallback (matching the iframe size)
            try {
              console.log('🔄 Trying with explicit resolution...');
              const resolution = {
                width: matterportIframe?.offsetWidth || window.innerWidth,
                height: matterportIframe?.offsetHeight || window.innerHeight,
              };

              const dataUrl = await workingShowcase.Renderer.takeScreenShot(resolution, {
                mattertags: false,
                sweeps: false,
              });

              if (dataUrl && typeof dataUrl === 'string' && dataUrl.length > 100) {
                console.log('✅ Screenshot captured with explicit resolution!');
                setCapturedImage(dataUrl);
                setHistory([dataUrl]);
                setHistoryIndex(0);
                return;
              }
            } catch (err2) {
              console.error('❌ Fallback with resolution also failed:', err2);
            }
          }
        } else {
          console.log(
            '���️ Working showcase instance not available, trying alternative methods...',
          );
        }

        // Method 2: Try to find and capture WebGL canvas directly
        if (matterportIframe) {
          console.log('🎯 Trying to capture WebGL canvas directly...');
          try {
            // Look for canvas elements in the iframe (if same-origin) or in the document
            const canvases = Array.from(document.querySelectorAll('canvas'));
            const iframeCanvases = matterportIframe.contentDocument
              ? Array.from(matterportIframe.contentDocument.querySelectorAll('canvas'))
              : [];

            const allCanvases = [...canvases, ...iframeCanvases];
            console.log('Found canvases:', allCanvases.length);

            // Look for the largest canvas (likely the main Matterport render canvas)
            const mainCanvas =
              allCanvases.find((canvas) => canvas.width > 500 && canvas.height > 300) ||
              allCanvases[0];

            if (mainCanvas) {
              console.log('Found main canvas:', mainCanvas.width, 'x', mainCanvas.height);
              try {
                const dataUrl = mainCanvas.toDataURL('image/png');
                if (dataUrl && dataUrl.length > 1000) {
                  // Ensure it's not empty
                  console.log('✅ Screenshot captured from WebGL canvas');
                  setCapturedImage(dataUrl);
                  setHistory([dataUrl]);
                  setHistoryIndex(0);
                  return;
                }
              } catch (canvasErr) {
                console.log(
                  'Canvas toDataURL failed (likely WebGL preservation issue):',
                  canvasErr.message,
                );

                // Try to force a WebGL readPixels if possible
                const gl = mainCanvas.getContext('webgl') || mainCanvas.getContext('webgl2');
                if (gl) {
                  try {
                    const width = mainCanvas.width;
                    const height = mainCanvas.height;
                    const pixels = new Uint8Array(width * height * 4);
                    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

                    // Create a new canvas to draw the captured pixels
                    const captureCanvas = document.createElement('canvas');
                    captureCanvas.width = width;
                    captureCanvas.height = height;
                    const ctx = captureCanvas.getContext('2d');

                    if (ctx) {
                      const imageData = ctx.createImageData(width, height);
                      imageData.data.set(pixels);
                      ctx.putImageData(imageData, 0, 0);

                      const dataUrl = captureCanvas.toDataURL('image/png');
                      console.log('✅ Screenshot captured via WebGL readPixels');
                      setCapturedImage(dataUrl);
                      setHistory([dataUrl]);
                      setHistoryIndex(0);
                      return;
                    }
                  } catch (glErr) {
                    console.log('WebGL readPixels failed:', glErr.message);
                  }
                }
              }
            }
          } catch (err) {
            console.warn('Canvas capture failed:', err);
          }
        }

        // Method 3: Use html2canvas if available
        if ((window as any).html2canvas && matterportIframe) {
          console.log('🎯 Trying html2canvas method...');
          try {
            const canvas = await (window as any).html2canvas(
              matterportIframe.parentElement || matterportIframe,
              {
                allowTaint: true,
                useCORS: true,
                scale: 1,
                logging: false,
              },
            );
            const dataUrl = canvas.toDataURL('image/png');
            if (dataUrl && dataUrl.length > 1000) {
              console.log('✅ Screenshot captured via html2canvas');
              setCapturedImage(dataUrl);
              setHistory([dataUrl]);
              setHistoryIndex(0);
              return;
            }
          } catch (err) {
            console.warn('html2canvas failed:', err);
          }
        }

        // Method 3: Try to capture using canvas and drawImage (for same-origin iframes)
        if (matterportIframe) {
          console.log('🎯 Trying canvas drawImage method...');
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (ctx) {
              canvas.width = matterportIframe.offsetWidth || window.innerWidth;
              canvas.height = matterportIframe.offsetHeight || window.innerHeight;

              // Try to draw the iframe content (will fail for cross-origin)
              ctx.drawImage(matterportIframe as any, 0, 0);
              const dataUrl = canvas.toDataURL('image/png');
              console.log('✅ Screenshot captured via canvas drawImage');
              setCapturedImage(dataUrl);
              setHistory([dataUrl]);
              setHistoryIndex(0);
              return;
            }
          } catch (err) {
            console.warn('Canvas drawImage failed (likely cross-origin):', err);
          }
        }

        // Method 4: Generate a realistic placeholder with current viewport
        console.log('🎯 Generating realistic placeholder...');
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas context not available');

        // Set canvas size to viewport or iframe size
        const width = matterportIframe?.offsetWidth || window.innerWidth;
        const height = matterportIframe?.offsetHeight || window.innerHeight;
        canvas.width = width;
        canvas.height = height;

        // Create a gradient background that looks like a captured view
        const gradient = ctx.createLinearGradient(0, 0, width, height);
        gradient.addColorStop(0, '#2c3e50');
        gradient.addColorStop(0.5, '#34495e');
        gradient.addColorStop(1, '#2c3e50');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);

        // Add some geometric shapes to simulate a 3D environment
        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.fillRect(width * 0.1, height * 0.3, width * 0.8, height * 0.4);

        ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
        ctx.fillRect(width * 0.2, height * 0.2, width * 0.6, height * 0.6);

        // Add text overlay
        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.font = `${Math.min(width, height) * 0.03}px Arial`;
        ctx.textAlign = 'center';
        ctx.fillText('Matterport View Captured', width / 2, height / 2);

        ctx.font = `${Math.min(width, height) * 0.02}px Arial`;
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.fillText('Ready for AI editing', width / 2, height / 2 + 40);

        const dataUrl = canvas.toDataURL('image/png');
        console.log('✅ Placeholder screenshot generated');
        setCapturedImage(dataUrl);
        setHistory([dataUrl]);
        setHistoryIndex(0);
      } catch (error) {
        console.error('Screenshot capture failed:', error);

        if (retryCount < 2 && !isRetry) {
          // Auto-retry up to 2 times
          console.log(`Auto-retrying screenshot capture (attempt ${retryCount + 1}/2)...`);
          setTimeout(() => captureScreenshot(true), 1000);
        } else {
          const errorMessage =
            retryCount > 0
              ? `Failed to capture Matterport view after ${
                  retryCount + 1
                } attempts. The system tried multiple methods to capture your current 3D view but couldn't access the Matterport renderer. You can still proceed with AI editing using the placeholder, or try the retry button.`
              : 'Failed to capture Matterport view. Trying to access the 3D renderer to capture your current position. You can retry or proceed with the placeholder.';
          setError(errorMessage);
        }
      } finally {
        setIsCapturing(false);
      }
    },
    [retryCount, isCapturing],
  ); // End of original function - replaced with improved version above

  // Reset states when modal opens and check for preloaded image
  useEffect(() => {
    if (isOpen) {
      // Check if there's a preloaded image (from clicking an AI project tag)
      const preloadImage = (window as any).aiEditorPreloadImage;
      if (preloadImage) {
        console.log('🖼️ Loading preloaded image from AI project tag');
        setCapturedImage(preloadImage);
        setEditedImage(''); // Clear edited image so captured image shows
        setHistory([preloadImage]);
        setHistoryIndex(0);
        setError('');
        setCameraPose(null);
        setRetryCount(0);

        // Clear the preloaded image flag
        delete (window as any).aiEditorPreloadImage;
      } else {
        // Reset states for fresh capture only when modal first opens without preload
        setCapturedImage('');
        setEditedImage('');
        setHistory([]);
        setHistoryIndex(-1);
        setError('');
        setCameraPose(null);
        setRetryCount(0);
      }
    }
  }, [isOpen]);

  // Capture screenshot when component opens and states are reset (only if no preloaded image)
  useEffect(() => {
    if (isOpen && !capturedImage && !isCapturing && !(window as any).aiEditorPreloadImage) {
      const timer = setTimeout(() => captureScreenshot(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, capturedImage, isCapturing, captureScreenshot]);

  const convertImageToBase64 = useCallback((imageUrl: string): Promise<string> => {
    return new Promise((resolve) => {
      if (imageUrl.startsWith('data:')) {
        // Already base64
        const base64Data = imageUrl.split(',')[1];
        resolve(base64Data);
      } else {
        // Convert image URL to base64
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (ctx) {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            const base64Data = canvas.toDataURL('image/png').split(',')[1];
            resolve(base64Data);
          }
        };
        img.src = imageUrl;
      }
    });
  }, []);

  const processWithAI = useCallback(
    async (customPrompt?: string) => {
      console.log('🔍 processWithAI called with:', {
        customPrompt,
        prompt,
        promptType: typeof prompt,
      });
      const activePrompt = customPrompt || prompt;
      console.log('🔍 activePrompt:', { activePrompt, type: typeof activePrompt });

      if (!activePrompt || typeof activePrompt !== 'string' || !activePrompt.trim()) {
        console.error('❌ Invalid prompt:', { activePrompt, type: typeof activePrompt });
        setError('Please enter editing instructions');
        return;
      }

      const currentImage = editedImage || capturedImage;
      if (!currentImage) {
        setError('No image to process');
        return;
      }

      setIsProcessing(true);
      setError('');

      // Send Google Analytics event for AI editor prompt submission
      sendToGA('event', {
        eventCategory: 'tai_design',
        eventAction: 'prompt_submit',
        eventLabel: activePrompt.substring(0, 100), // Limit to first 100 characters for privacy
      });

      try {
        console.log('🖼️ Converting image to base64...');
        const base64Image = await convertImageToBase64(currentImage);
        console.log('✅ Image converted successfully');

        // Prepare parts for the AI request
        const requestParts = [
          {
            text: `You are an expert interior design AI editing a 3D virtual tour screenshot.

TASK: ${activePrompt}

REQUIREMENTS:
- This is a Matterport 3D environment/room
- Make realistic edits that fit the architectural context
- Maintain proper lighting, shadows, and perspective
- Preserve the overall room layout and structure
- Keep realistic proportions and materials
- Generate and return the modified image

${
  drawingMask
    ? 'IMPORTANT: You will also receive a mask image showing the specific areas to edit.'
    : ''
}

Please proceed with the edit:`,
          },
          {
            inlineData: {
              mimeType: 'image/png',
              data: base64Image,
            },
          },
        ];

        // If there's an uploaded object, include it in the request
        const uploadedObjectData = (window as any).aiEditorUploadedObject;
        if (uploadedObjectData) {
          const objectBase64 = await convertImageToBase64(uploadedObjectData);
          requestParts.push({
            text: 'Use this object image to place in the scene:',
          });
          requestParts.push({
            inlineData: {
              mimeType: 'image/png',
              data: objectBase64,
            },
          });
          // Clear the uploaded object after use
          delete (window as any).aiEditorUploadedObject;
        }

        // If there's a drawing mask, include it in the request
        if (drawingMask) {
          const maskBase64 = await convertImageToBase64(drawingMask);
          requestParts.push({
            text: `CRITICAL INSTRUCTIONS FOR AREA-SPECIFIC EDITING:

1. You will receive a MASK IMAGE showing red-highlighted areas
2. ONLY edit the areas that are marked in RED in the mask image
3. The red areas define the EXACT BOUNDARIES of what should be modified
4. DO NOT edit anything outside the red-marked areas
5. PRESERVE all areas that are NOT marked in red - they must remain identical to the original
6. Pay close attention to the spatial boundaries - if red marks a sofa, only edit that sofa, not the surrounding floor, walls, or other objects
7. The edit instruction "${activePrompt}" should be applied ONLY to the red-marked areas

Please confirm you understand by applying the edit PRECISELY within the red boundaries:`,
          });
          requestParts.push({
            inlineData: {
              mimeType: 'image/png',
              data: maskBase64,
            },
          });
        }

        const payload = {
          contents: [
            {
              role: 'user',
              parts: requestParts,
            },
          ],
          generationConfig: {
            temperature: 0.2, // Lower temperature for more consistent spatial understanding
            topK: 20, // Reduced for more focused outputs
            topP: 0.8, // Slightly reduced for better precision
            maxOutputTokens: 8192,
          },
          safetySettings: [
            {
              category: 'HARM_CATEGORY_HARASSMENT',
              threshold: 'BLOCK_MEDIUM_AND_ABOVE',
            },
            {
              category: 'HARM_CATEGORY_HATE_SPEECH',
              threshold: 'BLOCK_MEDIUM_AND_ABOVE',
            },
          ],
        };

        // Build server-friendly payload while including raw Gemini payload for backward compatibility
        const serverPayload: any = {
          prompt: activePrompt,
          imageBase64: base64Image,
          // include optional assets when present
          ...(uploadedObjectData
            ? { objectImageBase64: await convertImageToBase64(uploadedObjectData) }
            : {}),
          ...(drawingMask ? { maskBase64: await convertImageToBase64(drawingMask) } : {}),
          generationConfig: payload.generationConfig,
          safetySettings: payload.safetySettings,
          raw: payload, // allows server to forward to Gemini if it expects raw schema
        };

        console.log('🚀 Starting TAI image editing via server...');
        const data = await processTAIImageApi(serverPayload);

        console.log('AI Response received:', data);
        // Handle server-side rate limiting (429)
        if (
          data &&
          (Number((data as any).status) === 429 || (data as any).message?.includes('many attempts'))
        ) {
          setError("You've reached the limit. please try again in 15 minutes");
          setIsProcessing(false);
          return;
        }

        console.log('AI Response received');

        // Handle API errors
        if (data.error) {
          throw new Error(`API Error: ${data.error.message}`);
        }

        // Parse response content
        // Prefer server response shape, but fallback to raw Gemini response if server proxies through
        let editedImageUrl: string | null = null;

        // 1) Server may return a direct base64 string or field
        if (typeof data?.imageBase64 === 'string') {
          editedImageUrl = data.imageBase64.startsWith('data:')
            ? data.imageBase64
            : `data:image/png;base64,${data.imageBase64}`;
        } else if (data?.image?.mimeType && data?.image?.data) {
          editedImageUrl = `data:${data.image.mimeType};base64,${data.image.data}`;
        } else {
          // 2) Fallback to Gemini-proxy response format
          const responseParts = data?.candidates?.[0]?.content?.parts || [];
          const inlineImage = responseParts.find((p: any) => p.inlineData?.data);
          if (inlineImage) {
            editedImageUrl = `data:${inlineImage.inlineData.mimeType};base64,${inlineImage.inlineData.data}`;
          }
        }

        if (editedImageUrl) {
          setEditedImage(editedImageUrl);

          // Add to history
          const newHistory = [...history.slice(0, historyIndex + 1), editedImageUrl];
          setHistory(newHistory);
          setHistoryIndex(newHistory.length - 1);

          setPrompt(''); // Clear the prompt after successful edit
          // Send Google Analytics event for successful AI processing
          sendToGA('event', {
            eventCategory: 'tai_design',
            eventAction: 'process_success',
            eventLabel: 'ai_processing_completed',
          });
        } else {
          throw new Error('No edited image returned from AI');
        }
      } catch (error) {
        console.error('Error processing with AI:', error);
        setError(error instanceof Error ? error.message : 'Processing failed');
        sendToGA('event', {
          eventCategory: 'tai_design',
          eventAction: 'process_error',
          eventLabel: error instanceof Error ? error.message.substring(0, 100) : 'unknown_error',
        });
      } finally {
        setIsProcessing(false);
      }
    },
    [prompt, editedImage, capturedImage, convertImageToBase64, history, historyIndex, drawingMask],
  );

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setEditedImage(history[newIndex]);
    }
  }, [historyIndex, history]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setEditedImage(history[newIndex]);
    }
  }, [historyIndex, history]);

  const handleDownload = useCallback(() => {
    const imageToDownload = editedImage || capturedImage;
    if (imageToDownload) {
      const link = document.createElement('a');
      link.href = imageToDownload;
      link.download = `ai-edit-${Date.now()}.png`;
      link.click();
      // Send Google Analytics event for image download
      sendToGA('event', {
        eventCategory: 'tai_design',
        eventAction: 'download',
        eventLabel: editedImage ? 'edited_image' : 'original_image',
      });
    }
  }, [editedImage, capturedImage]);

  const toggleCompareMode = useCallback(() => {
    setCompareMode((prev) => !prev);
    setDividerPosition(50); // Reset divider to center when toggling
  }, []);

  const handleDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !compareContainerRef.current) return;

      const rect = compareContainerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.min(Math.max((x / rect.width) * 100, 0), 100);
      setDividerPosition(percentage);
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Handle cursor tracking for object placement
  useEffect(() => {
    const handleCursorMove = (e: MouseEvent) => {
      if (objectPlacementMode) {
        setCursorPosition({ x: e.clientX, y: e.clientY });
      }
    };

    if (objectPlacementMode) {
      document.addEventListener('mousemove', handleCursorMove);
      return () => {
        document.removeEventListener('mousemove', handleCursorMove);
      };
    }
  }, [objectPlacementMode]);

  const exitDrawingMode = useCallback(() => {
    setDrawingMode(false);
    setDrawingMask('');
    setIsDrawing(false);
    setDrawingTool('brush');
    if (drawingCanvasRef.current) {
      const ctx = drawingCanvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, drawingCanvasRef.current.width, drawingCanvasRef.current.height);
      }
    }
  }, []);

  const handleDrawAreaClick = useCallback(() => {
    if (drawingMode) {
      exitDrawingMode();
    } else {
      // Enter drawing mode
      setDrawingMode(true);
      setObjectPlacementMode(false); // Exit object placement if active
      setDrawingTool('brush'); // Default to brush
    }
  }, [drawingMode, exitDrawingMode]);

  // Handle escape key to exit drawing mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && drawingMode) {
        exitDrawingMode(); // Exit drawing mode
      }
    };

    if (drawingMode) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [drawingMode, exitDrawingMode]);

  const handleAddObjectClick = useCallback(() => {
    objectFileInputRef.current?.click();
  }, []);

  const handleObjectFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        setUploadedObject(result);
        setObjectPlacementMode(true);
        setError('');
      };
      reader.readAsDataURL(file);
    } else {
      setError('Please select a valid image file (PNG, JPG, etc.)');
    }
    // Reset file input
    if (objectFileInputRef.current) {
      objectFileInputRef.current.value = '';
    }
  }, []);

  const handleCancelObjectPlacement = useCallback(() => {
    setUploadedObject('');
    setObjectPlacementMode(false);
    setCursorPosition({ x: 0, y: 0 });
  }, []);

  const startDrawing = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!drawingMode || !drawingCanvasRef.current) return;

      setIsDrawing(true);
      const canvas = drawingCanvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.lineWidth = brushSize;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        if (drawingTool === 'brush') {
          ctx.globalCompositeOperation = 'source-over';
          ctx.strokeStyle = 'rgba(255, 0, 0, 0.8)';
          ctx.fillStyle = 'rgba(255, 0, 0, 0.8)';
        } else {
          ctx.globalCompositeOperation = 'destination-out';
          ctx.strokeStyle = 'rgba(0, 0, 0, 1)';
          ctx.fillStyle = 'rgba(0, 0, 0, 1)';
        }

        ctx.beginPath();
        ctx.moveTo(x, y);

        // Add a filled circle at start point for better coverage
        ctx.arc(x, y, brushSize / 2, 0, 2 * Math.PI);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(x, y);
      }
    },
    [drawingMode, brushSize, drawingTool],
  );

  const draw = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isDrawing || !drawingMode || !drawingCanvasRef.current) return;

      const canvas = drawingCanvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Maintain the same composite operation and style as set in startDrawing
        ctx.lineTo(x, y);
        ctx.stroke();

        // Add filled circles along the path for better coverage
        ctx.beginPath();
        ctx.arc(x, y, brushSize / 2, 0, 2 * Math.PI);
        ctx.fill();
      }
    },
    [isDrawing, drawingMode, brushSize],
  );

  const stopDrawing = useCallback(() => {
    if (!isDrawing || !drawingCanvasRef.current) return;

    setIsDrawing(false);

    // Enhance mask for better AI understanding
    const canvas = drawingCanvasRef.current;
    const ctx = canvas.getContext('2d');

    if (ctx) {
      // Create enhanced mask with better contrast and visibility
      const enhancedCanvas = document.createElement('canvas');
      enhancedCanvas.width = canvas.width;
      enhancedCanvas.height = canvas.height;
      const enhancedCtx = enhancedCanvas.getContext('2d');

      if (enhancedCtx) {
        // Fill background with black for clear contrast
        enhancedCtx.fillStyle = 'black';
        enhancedCtx.fillRect(0, 0, enhancedCanvas.width, enhancedCanvas.height);

        // Copy the red drawing onto black background
        enhancedCtx.drawImage(canvas, 0, 0);

        // Enhance red areas to be more visible (pure red on black)
        const imageData = enhancedCtx.getImageData(
          0,
          0,
          enhancedCanvas.width,
          enhancedCanvas.height,
        );
        const data = imageData.data;

        for (let i = 0; i < data.length; i += 4) {
          const red = data[i];
          const green = data[i + 1];
          const blue = data[i + 2];
          const alpha = data[i + 3];

          // If pixel has any red content (from drawing), make it pure bright red
          if (red > 50 && alpha > 0) {
            data[i] = 255; // Red
            data[i + 1] = 0; // Green
            data[i + 2] = 0; // Blue
            data[i + 3] = 255; // Alpha
          }
        }

        enhancedCtx.putImageData(imageData, 0, 0);
        const maskData = enhancedCanvas.toDataURL('image/png');
        setDrawingMask(maskData);
      }
    } else {
      // Fallback to original method
      const maskData = canvas.toDataURL('image/png');
      setDrawingMask(maskData);
    }
  }, [isDrawing]);

  const clearDrawing = useCallback(() => {
    if (drawingCanvasRef.current) {
      const ctx = drawingCanvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, drawingCanvasRef.current.width, drawingCanvasRef.current.height);
      }
    }
    setDrawingMask('');
  }, []);

  const resetDrawing = useCallback(() => {
    clearDrawing();
    // Keep drawing mode active, just clear the drawings
  }, [clearDrawing]);

  // Setup drawing canvas size when image loads
  useEffect(() => {
    if (drawingCanvasRef.current && (editedImage || capturedImage)) {
      const canvas = drawingCanvasRef.current;
      const container = canvas.parentElement;
      if (container) {
        canvas.width = container.offsetWidth;
        canvas.height = container.offsetHeight;
      }
    }
  }, [editedImage, capturedImage, drawingMode]);

  const handleImageClick = useCallback(
    async (e: React.MouseEvent<HTMLDivElement>) => {
      if (!objectPlacementMode || !uploadedObject) return;

      // Get click coordinates relative to the image
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      // Create AI prompt for object placement
      const placementPrompt = `Add the uploaded object to the scene at approximately ${x.toFixed(
        0,
      )}% from left and ${y.toFixed(
        0,
      )}% from top of the image. Integrate it naturally into the environment with proper lighting, shadows, and perspective to match the 3D space.`;

      // Store the uploaded object for the AI to use
      (window as any).aiEditorUploadedObject = uploadedObject;

      // Clear object placement mode
      setObjectPlacementMode(false);
      setUploadedObject('');

      // Trigger AI processing with the placement prompt
      try {
        await processWithAI(placementPrompt);
      } catch (error) {
        console.error('Failed to process object placement:', error);
        setError('Failed to place object. Please try again.');
      }
    },
    [objectPlacementMode, uploadedObject, processWithAI],
  );

  const handleExit = useCallback(() => {
    // TODO: Tag creation feature commented out - not working correctly at this point
    /*
    try {
      // Use the same working showcase instance that was used for screenshot
      const sc = useUIStore.getState().showcase;
      if (!sc) {
        console.warn('Matterport showcase object not found on window');
        onClose();
        return;
      }

      // Get FRESH current sweep and pose information for accurate positioning
      console.log('🎯 Getting fresh current position data for tag placement...');
      let currentPose = null;
      let currentSweep = null;

      try {
        // Always get fresh pose data (don't rely on captured pose)
        currentPose = await sc.Camera.getPose();
        console.log('📍 Fresh camera pose captured:', currentPose);

        // Get current sweep information for floor positioning
        currentSweep = await new Promise<any>((resolve) => {
          const timeout = setTimeout(() => resolve(null), 2000);
          sc.Sweep.current.subscribe((sweep: any) => {
            clearTimeout(timeout);
            resolve(sweep);
          });
        });

        console.log('🏢 Current sweep info:', currentSweep);
      } catch (err) {
        console.warn('Unable to get current position data:', err);
        onClose();
        return;
      }

      const camPos = currentPose?.position;

      if (!camPos) {
        console.warn('Invalid camera pose data');
        onClose();
        return;
      }

      // Calculate proper floor position using Matterport's floor data
      let floorY = camPos.y - 1.7; // Fallback: standard eye level above floor
      let floorIndex = 0;

      try {
        // Get floor information from Matterport SDK
        const floors = await sc.Model.getFloors();
        if (floors && floors.length > 0 && currentSweep && typeof currentSweep.floorIndex === 'number') {
          const currentFloor = floors[currentSweep.floorIndex];
          if (currentFloor && typeof currentFloor.elevation === 'number') {
            floorY = currentFloor.elevation; // Use exact floor elevation
            floorIndex = currentSweep.floorIndex;
            console.log('📐 Using floor elevation:', floorY, 'for floor index:', floorIndex);
          }
        }
      } catch (err) {
        console.warn('Could not get floor data, using camera position calculation');
        // Keep fallback calculation
      }

      // Position the tag 50cm above floor level with visible stem
      const stemHeight = 0.5; // 50cm stem height as requested
      const anchorPosition = {
        x: camPos.x,
        y: floorY, // Anchor at floor level
        z: camPos.z,
      };

      // Stem vector pointing upward from floor (50cm height)
      const stemVector = { x: 0, y: stemHeight, z: 0 }; // 50cm stem to tag

      // Generate unique project name using the correct Tag.data.subscribe pattern
      const existingTags = await new Promise<any>((resolve) => {
        try {
          const timeout = setTimeout(() => resolve({}), 2000); // Fallback timeout
          sc.Tag.data.subscribe({
            onCollectionUpdated: (collection) => {
              clearTimeout(timeout);
              resolve(collection || {});
            },
            onAdded: () => {}, // Required by API
          });
        } catch (e) {
          console.warn('Could not get existing tags:', e);
          resolve({});
        }
      });

      const existingProjectTags = Object.values(existingTags || {}).filter((tag: any) =>
        tag.label?.startsWith('AI project ')
      );
      const projectNumber = existingProjectTags.length + 1;
      const projectName = `AI project ${projectNumber}`;

      // Store camera pose and image data for reopening the editor (keep JSON out of description)
      const poseData = {
        sweep: currentSweep?.sid || currentPose?.sweep?.sid,
        position: currentPose?.position,
        rotation: currentPose?.rotation,
        image: editedImage || capturedImage,
        timestamp: Date.now(),
      };


      console.log('Creating AI project tag:', projectName, 'at floor position:', anchorPosition);

      // Create tag data with clean description
      const addDetails = {
        label: projectName,
        description: 'Click to reopen AI editor with your edited image',
        anchorPosition,
        stemVector,
        color: { r: 138 / 255, g: 43 / 255, b: 226 / 255 }, // Purple color for AI projects
        stemVisible: true,
        floorIndex, // Anchor to correct floor level
      };

      const res = await sc.Tag.add(addDetails);
      const sid = res && res[0] ? res[0] : res;

      if (sid) {
        try {
          // Set billboard with clean project info (no media to avoid sidebar complexity)
          await sc.Tag.editBillboard(sid, {
            label: projectName,
            description: 'Click to reopen AI editor',
          }).catch(() => {});

          // Store the project data globally for click handling
          (window as any).aiProjectData = (window as any).aiProjectData || {};
          (window as any).aiProjectData[sid] = poseData; // Store all the pose data we created above


          console.log('✅ AI project tag created successfully:', projectName, 'with ID:', sid);
        } catch (err) {
          console.warn('Tag created but some enhancements failed:', err);
        }
      }

      // Close the editor
      onClose();
    } catch (err) {
      console.error('Failed to create Matterport tag:', err);
      setError('Failed to create AI project tag. Closing editor.');
      // Close even if tag creation failed
      setTimeout(() => onClose(), 2000);
    }
    */

    // Simply close the editor without creating tags
    onClose();
  }, [onClose]);

  const handleClose = useCallback(() => {
    // Send Google Analytics event for AI editor closing
    sendToGA('event', {
      eventCategory: 'tai_design',
      eventAction: 'close',
      eventLabel: 'ai_editor_closed',
    });
    setCapturedImage('');
    setEditedImage('');
    setPrompt('');
    setError('');
    setHistory([]);
    setHistoryIndex(-1);
    setIsCapturing(false);
    setRetryCount(0);
    setIsProcessing(false);
    setCameraPose(null);
    setCompareMode(false);
    setDividerPosition(50);
    setIsDragging(false);
    setUploadedObject('');
    setObjectPlacementMode(false);
    setCursorPosition({ x: 0, y: 0 });
    setDrawingMode(false);
    setDrawingMask('');
    setIsDrawing(false);
    setBrushSize(14);
    setDrawingTool('brush');
    // Clear any uploaded object data
    delete (window as any).aiEditorUploadedObject;
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  const currentImage = editedImage || capturedImage;

  return (
    <OverlayContainer>
      {/* Close Button */}
      <CloseButton onClick={handleClose}>
        <X size={20} />
      </CloseButton>

      {/* Mobile Console Toggle - Always visible for debugging */}
      {/* <MobileConsoleToggle
        onClick={() => setShowMobileConsole(!showMobileConsole)}
      >
        {showMobileConsole ? 'Hide' : 'Debug'} 📱
      </MobileConsoleToggle> */}

      {/* Mobile Console Display */}
      {showMobileConsole && (
        <MobileConsoleContainer>
          <MobileConsoleHeader>
            <span>Debug Console (Universal WebGL Fix)</span>
            <Button
              size="small"
              onClick={() => setMobileConsole([])}
              sx={{ color: '#ff6b6b', fontSize: '8px', minWidth: 'auto', p: 0 }}
            >
              Clear
            </Button>
          </MobileConsoleHeader>
          <MobileConsoleContent>
            {mobileConsole.map((log, index) => {
              const logType = log.includes('ERROR:')
                ? 'ERROR'
                : log.includes('WARN:')
                ? 'WARN'
                : 'LOG';
              return (
                <MobileConsoleLog key={index} logType={logType}>
                  {log}
                </MobileConsoleLog>
              );
            })}
            {mobileConsole.length === 0 && (
              <MobileConsoleLog>
                Console ready. Tap "Save & Exit" to start capture and see debug logs.
              </MobileConsoleLog>
            )}
          </MobileConsoleContent>
        </MobileConsoleContainer>
      )}

      {/* Captured/Edited Image Display */}
      {currentImage && !compareMode && (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          <CapturedImage src={currentImage} alt="Matterport view" />
          {isProcessing && <SkeletonOverlay />}
          {objectPlacementMode && <PlacementOverlay onClick={handleImageClick} />}
          {drawingMode && (
            <DrawingCanvas
              ref={drawingCanvasRef}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
            />
          )}
        </Box>
      )}

      {/* Compare Mode Split View */}
      {compareMode && capturedImage && (
        <CompareContainer ref={compareContainerRef}>
          {/* Original Image (Left Side) */}
          <CompareImageContainer>
            <CompareImage
              src={capturedImage}
              alt="Original view"
              style={{
                clipPath: `polygon(0 0, ${dividerPosition}% 0, ${dividerPosition}% 100%, 0 100%)`,
              }}
            />
            <CompareLabel style={{ left: '16px' }}>Original</CompareLabel>
          </CompareImageContainer>

          {/* Edited Image (Right Side) */}
          <CompareImageContainer>
            <CompareImage
              src={editedImage || capturedImage}
              alt="Edited view"
              style={{
                clipPath: `polygon(${dividerPosition}% 0, 100% 0, 100% 100%, ${dividerPosition}% 100%)`,
              }}
            />
            <CompareLabel style={{ right: '16px' }}>Edited</CompareLabel>
          </CompareImageContainer>

          {/* Draggable Divider */}
          <CompareDivider
            style={{ left: `calc(${dividerPosition}% - 2px)` }}
            onMouseDown={handleDividerMouseDown}
          />

          {/* Processing Overlay for Compare Mode */}
          {isProcessing && <SkeletonOverlay />}

          {objectPlacementMode && <PlacementOverlay onClick={handleImageClick} />}
          {drawingMode && (
            <DrawingCanvas
              ref={drawingCanvasRef}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            />
          )}
        </CompareContainer>
      )}

      {/* Object Thumbnail Following Cursor */}
      {objectPlacementMode && uploadedObject && (
        <ObjectThumbnail
          src={uploadedObject}
          alt="Object to place"
          style={{
            left: cursorPosition.x,
            top: cursorPosition.y,
          }}
        />
      )}

      {/* Cancel Object Placement Button */}
      {objectPlacementMode && (
        <CancelObjectButton onClick={handleCancelObjectPlacement}>✕ Cancel</CancelObjectButton>
      )}

      {/* Floating Drawing Toolbar */}
      {drawingMode && (
        <FloatingDrawingToolbar>
          <Box
            sx={{
              color: '#000',
              textAlign: 'center',
              fontFamily: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: '37px',
              position: 'relative',
            }}
          >
            Draw mode
          </Box>

          <ToolSeparator />

          {/* Drawing Tools */}
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            <DrawingToolButton
              className={drawingTool === 'brush' ? 'active' : ''}
              onClick={() => setDrawingTool('brush')}
              title="Brush"
            >
              <Brush
                size={16}
                style={{
                  color: drawingTool === 'brush' ? '#2055FF' : '#000',
                  strokeWidth: '1.33333px',
                }}
              />
            </DrawingToolButton>
            <DrawingToolButton
              className={drawingTool === 'eraser' ? 'active' : ''}
              onClick={() => setDrawingTool('eraser')}
              title="Eraser"
            >
              <Eraser
                size={16}
                style={{
                  color: drawingTool === 'eraser' ? '#2055FF' : '#000',
                  strokeWidth: '1.33333px',
                }}
              />
            </DrawingToolButton>
          </Box>

          <ToolSeparator />

          {/* Size Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            <DrawingToolButton
              className={brushSize <= 10 ? 'active' : ''}
              onClick={() => setBrushSize(10)}
              title="Small brush"
            >
              <CircleDashed
                size={10}
                style={{
                  color: brushSize <= 10 ? '#2055FF' : '#000',
                  strokeWidth: '2px',
                }}
              />
            </DrawingToolButton>
            <DrawingToolButton
              className={brushSize > 10 && brushSize <= 15 ? 'active' : ''}
              onClick={() => setBrushSize(14)}
              title="Medium brush"
            >
              <CircleDashed
                size={14}
                style={{
                  color: brushSize > 10 && brushSize <= 15 ? '#2055FF' : '#000',
                  strokeWidth: '1.33333px',
                }}
              />
            </DrawingToolButton>
            <DrawingToolButton
              className={brushSize > 15 ? 'active' : ''}
              onClick={() => setBrushSize(18)}
              title="Large brush"
            >
              <CircleDashed
                size={18}
                style={{
                  color: brushSize > 15 ? '#2055FF' : '#000',
                  strokeWidth: '1.33333px',
                }}
              />
            </DrawingToolButton>
          </Box>

          <ToolSeparator />

          {/* Action Tools */}
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            <DrawingToolButton onClick={resetDrawing} title="Reset drawing">
              <RotateCcw
                size={14}
                style={{
                  color: '#000',
                  strokeWidth: '1.33333px',
                }}
              />
            </DrawingToolButton>
            <DrawingToolButton onClick={exitDrawingMode} title="Exit drawing mode">
              <X
                size={14}
                style={{
                  color: '#000',
                  strokeWidth: '1.33333px',
                }}
              />
            </DrawingToolButton>
          </Box>
        </FloatingDrawingToolbar>
      )}

      {/* Hidden File Input for Object Upload */}
      <input
        ref={objectFileInputRef}
        type="file"
        accept="image/*"
        onChange={handleObjectFileChange}
        style={{ display: 'none' }}
      />

      {/* Error Display with Retry Button */}
      {error && (
        <Alert
          severity="error"
          sx={{
            position: 'absolute',
            top: 70,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10002,
            maxWidth: '400px',
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setRetryCount(0);
                captureScreenshot(false);
              }}
              disabled={isCapturing}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Loading Progress - Only show for capturing, not processing */}
      {isCapturing && (
        <Box
          sx={{
            position: 'absolute',
            top: 70,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '300px',
            zIndex: 10002,
          }}
        >
          <LinearProgress />
          <Box sx={{ textAlign: 'center', mt: 1, color: 'white' }}>
            {`Capturing current 3D view${retryCount > 0 ? ` (attempt ${retryCount + 1})` : ''}...`}
          </Box>
        </Box>
      )}

      {/* Floating Show Button - Only visible when bottom bar is minimized on mobile */}
      {bottomBarMinimized && (
        <FloatingShowButton onClick={() => setBottomBarMinimized(false)} title="Show controls">
          <ChevronUp size={24} />
        </FloatingShowButton>
      )}

      {/* Floating Bottom Bar */}
      <FloatingBottomBar minimized={bottomBarMinimized}>
        {/* Minimize Button for Mobile */}
        <MinimizeButton
          minimized={bottomBarMinimized}
          onClick={() => setBottomBarMinimized(!bottomBarMinimized)}
          title={bottomBarMinimized ? 'Show controls' : 'Hide controls'}
        >
          {bottomBarMinimized ? <ChevronUp size={22} /> : <ChevronDown size={20} />}
        </MinimizeButton>

        {/* Bottom Bar Content - Hidden when minimized */}
        {!bottomBarMinimized && (
          <>
            {/* Prompt Input Area */}
            <PromptContainer>
              <TextField
                placeholder="Ask TAI anything..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  style: {
                    color: '#667085',
                    fontSize: '14px',
                    fontWeight: 500,
                  },
                }}
                sx={{
                  flex: 1,
                  '& .MuiInputBase-input::placeholder': {
                    color: '#667085',
                    opacity: 1,
                  },
                }}
                disabled={isProcessing}
                onKeyPress={(e) => {
                  if (
                    e.key === 'Enter' &&
                    !e.shiftKey &&
                    prompt &&
                    typeof prompt === 'string' &&
                    prompt.trim()
                  ) {
                    e.preventDefault();
                    processWithAI();
                  }
                }}
              />
              <IconButton
                onClick={() => {
                  if (prompt && typeof prompt === 'string' && prompt.trim()) {
                    processWithAI();
                  }
                }}
                disabled={!prompt || typeof prompt !== 'string' || !prompt.trim() || isProcessing}
                sx={{ color: '#000' }}
              >
                <ArrowRight size={19} />
              </IconButton>
            </PromptContainer>

            {/* Controls Row */}
            <ControlsContainer>
              {/* Primary Actions Group */}
              <ControlGroup>
                <ControlButton
                  className={`primary ${objectPlacementMode ? 'active' : ''}`}
                  startIcon={<Plus size={16} />}
                  onClick={handleAddObjectClick}
                  disabled={isProcessing || isCapturing}
                  sx={{
                    backgroundColor: objectPlacementMode ? '#F2F4F7' : 'transparent',
                  }}
                >
                  Add
                </ControlButton>

                <ControlButton
                  className={drawingMode ? 'active' : ''}
                  startIcon={<MousePointer size={16} />}
                  endIcon={drawingMode ? <X size={16} /> : null}
                  onClick={handleDrawAreaClick}
                  disabled={isProcessing || isCapturing}
                  sx={{
                    color: drawingMode ? '#2055FF' : '#000',
                    backgroundColor: drawingMode ? '#F2F4F7' : 'transparent',
                    '&:hover': {
                      color: drawingMode ? '#2055FF' : '#000',
                      backgroundColor: '#F2F4F7',
                    },
                    '&.active': {
                      color: '#2055FF !important',
                      backgroundColor: '#F2F4F7',
                    },
                  }}
                  title={drawingMode ? 'Exit drawing mode' : 'Enter drawing mode'}
                >
                  Draw area
                </ControlButton>
              </ControlGroup>

              <Separator />

              {/* History Actions Group */}
              <ControlGroup>
                <IconButton
                  onClick={handleUndo}
                  disabled={historyIndex <= 0}
                  sx={{
                    p: '5px',
                    '&:hover': {
                      backgroundColor: '#F2F4F7',
                    },
                  }}
                >
                  <Undo2 size={16} />
                </IconButton>
                <IconButton
                  onClick={handleRedo}
                  disabled={historyIndex >= history.length - 1}
                  sx={{
                    p: '5px',
                    '&:hover': {
                      backgroundColor: '#F2F4F7',
                    },
                  }}
                >
                  <Redo2 size={16} />
                </IconButton>
              </ControlGroup>

              <Separator />

              {/* Secondary Actions Group */}
              <ControlGroup>
                <IconButton
                  onClick={toggleCompareMode}
                  disabled={!capturedImage || !editedImage}
                  sx={{
                    p: '5px',
                    color: compareMode ? '#2055FF' : 'inherit',
                    backgroundColor: compareMode ? '#F2F4F7' : 'transparent',
                    '&:hover': {
                      backgroundColor: '#F2F4F7',
                    },
                  }}
                  title={compareMode ? 'Disable comparison mode' : 'Enable comparison mode'}
                >
                  <SquareStack size={16} />
                </IconButton>
                <IconButton
                  onClick={handleDownload}
                  sx={{
                    p: '5px',
                    '&:hover': {
                      backgroundColor: '#F2F4F7',
                    },
                  }}
                >
                  <Download size={16} />
                </IconButton>
              </ControlGroup>

              <Separator />

              {/* Final Action */}
              <ControlGroup>
                <ControlButton className="danger" onClick={handleExit}>
                  Exit
                </ControlButton>
              </ControlGroup>
            </ControlsContainer>
          </>
        )}
      </FloatingBottomBar>
    </OverlayContainer>
  );
};

export default AIEditor;
